import { createContext, FC, JSX, useEffect, useRef, useState } from "react";
import { CreateExceptionsWizardContextSteps } from "@app/exceptions/contexts/interfaces";
import { CreateExceptionsState, CreateExceptionsSpecialty, CreateExceptionsProject, SupplementaryDocument } from "@app/exceptions/interfaces";
import ResourceFiltrationService, { FilterItemsGroupModel } from "@app/projects/resource-filtration/resourceFiltrationService";
import { FilterItem, FilterItemsGroup } from "@app/resource-filters/filter-items";
import { FilterItemsGroupOperation } from "@app/resource-filters/common";
import { SpecialtyGroupExceptionReviewStatus, SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { QESButton } from "@app/shared/qes-standards/Button";
import { FilterSetItem } from "@app/resource-filters/filter-sets/filterSetItem";

/* oxlint-disable sort-keys */
interface CreateExceptionsContextProps {
  attachments: SupplementaryDocument[];
  error: string | JSX.Element;
  loading: boolean;
  page: CreateExceptionsWizardContextSteps;
  serviceAreas: CreateExceptionsState[];
  selectedServiceArea: string;
  selectedSpecialty: string;
  projects: CreateExceptionsProject[];
  showChangeWarning: boolean;
  specialties: CreateExceptionsSpecialty[];
  status: SpecialtyGroupExceptionStatus;
  startDate: string;
  endDate: string;
  excludeFromCCIIOJustification: boolean;
  previouslyApproved: boolean;
  comment: string;
  narrative: string;
  resetFileUploader: boolean;
  reviewStatus: SpecialtyGroupExceptionReviewStatus;
  setPage: React.Dispatch<React.SetStateAction<CreateExceptionsWizardContextSteps>>;
  setAttachments: React.Dispatch<React.SetStateAction<SupplementaryDocument[]>>;
  setResetFileUploader: (resetFileUploader: boolean) => void;
  setSelectedServiceArea: (serviceArea: string) => void;
  setSelectedSpecialty: (specialty: string) => void;
  setShowChangeWarning: React.Dispatch<React.SetStateAction<boolean>>;
  setSpecialties: (specialties: CreateExceptionsSpecialty[]) => void;
  setStatus: (status: SpecialtyGroupExceptionStatus) => void;
  setStartDate: (startDate: string) => void;
  setEndDate: (endDate: string) => void;
  setExcludeFromCCIIOJustification: (excludeFromCCIIOJustification: boolean) => void;
  setPreviouslyApproved: (previouslyApproved: boolean) => void;
  setComment: (comment: string) => void;
  setNarrative: (narrative: string) => void;
  setReviewStatus: (reviewStatus: SpecialtyGroupExceptionReviewStatus) => void;
  updateRegionVisibility: (filter: string) => void;
  updateSpecialtiesVisibility: (filter: string) => void;
  updateProjectsVisibility: (filter: string) => void;
  updateSelectedServiceArea: (state: string, county: string, regionSetId: number) => void;
  updateSelectedSpecialty: (specialtyDisplayName: string, specialtyGroupCode: string) => void;
  updateSelectedProjects: (ids: string[]) => void;
}
/* oxlint-enable sort-keys */

const PUBLISHED_ONLY = true;
const SHOW_ZIP_SUMMARIES_ONLY = false;

const CreateExceptionsContext = createContext<Partial<CreateExceptionsContextProps>>({});

const CreateExceptionsProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const resourceFiltrationService = new ResourceFiltrationService();
  const serviceAreaHasChanged = useRef(false);
  const specialtyHasChanged = useRef(false);
  const projectsHasChanged = useRef(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<CreateExceptionsWizardContextSteps>(null);
  const [error, setError] = useState<string | JSX.Element>("");
  const [selectedServiceArea, setSelectedServiceArea] = useState<string>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>(null);
  const [resetFileUploader, setResetFileUploader] = useState<boolean>(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);

  // step 1 - Service Area
  const [serviceAreas, setServiceAreas] = useState<CreateExceptionsState[]>([]);

  // step 2 - Specialty
  const [specialties, setSpecialties] = useState<CreateExceptionsSpecialty[]>([]);

  // step 3 - Project(s)
  const [projects, setProjects] = useState<CreateExceptionsProject[]>([]);

  // step 4 - Details
  const [status, setStatus] = useState<SpecialtyGroupExceptionStatus>(SpecialtyGroupExceptionStatus.Pending);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [previouslyApproved, setPreviouslyApproved] = useState<boolean>(false);
  const [excludeFromCCIIOJustification, setExcludeFromCCIIOJustification] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [narrative, setNarrative] = useState<string>("");
  const [reviewStatus, setReviewStatus] = useState<SpecialtyGroupExceptionReviewStatus>(SpecialtyGroupExceptionReviewStatus.None);

  // step 5 - Attachments
  const [attachments, setAttachments] = useState<SupplementaryDocument[]>(new Array<SupplementaryDocument>());

  useEffect(() => {
    getServiceAreas();
    setPage(CreateExceptionsWizardContextSteps.ServiceArea);
    setProjects([]);
  }, []);

  useEffect(() => {
    switch (page) {
      case CreateExceptionsWizardContextSteps.Specialty:
        refreshFilteredSpecialties();
        break;
      case CreateExceptionsWizardContextSteps.Project:
        refreshFilteredProjects();
        break;
    }
  }, [page]);

  const getServiceAreas = () => {
    resourceFiltrationService.getStatesAndCounties(false, false).then((response: CreateExceptionsState[]) => {
      response.forEach((state) => {
        state.isVisible = true;
        state.childServiceAreas.forEach((county) => {
          county.isVisible = true;
        });
      })
      setServiceAreas(response);
      setLoading(false);
    }).catch(() => {
      setError(<>We were unable to retrieve the service areas used by your projects. <QESButton qesType="secondary" className="p-0" qesTestId="create_exceptions_wizard__refresh" onClick={() => { resetWizard() }}>Refresh</QESButton> to try again.</>);
    });
  };

  const makeParameterForSpecialties = () => {
    const stateAndCountyFilter = getServiceAreaFilter();
    const filterItemsGroup: FilterItemsGroup[] = [
      {
        filters: stateAndCountyFilter,
        operation: FilterItemsGroupOperation.LogicalOr,
      }
    ];
    return filterItemsGroup;
  }

  const makeParameterForProjects = () => {
    const serviceAreaFilter = getServiceAreaFilter();
    const serviceAreaString = serviceAreaFilter.map((stateOrCounty) => stateOrCounty.namespacePath);
    const specialtyFilter = getSpecialtyFilter();
    const specialtyFilterString = specialtyFilter.map((specialty) => specialty.namespacePath);

    const filterSetItems: FilterItemsGroupModel[] = [];
    serviceAreaString.length > 0 && filterSetItems.push({ filterItems: serviceAreaString, operation: FilterItemsGroupOperation.LogicalOr });
    specialtyFilterString.length > 0 && filterSetItems.push({ filterItems: specialtyFilterString, operation: FilterItemsGroupOperation.LogicalOr });
    return filterSetItems;
  }

  const refreshFilteredSpecialties = () => {
    if (!serviceAreaHasChanged.current && specialties.length !== 0) {
      return;
    }
    setLoading(true);

    const handleSpecialtiesResponse = (response: FilterItem[]) => {
      setSpecialties(response.map((specialty) => ({
        displayName: specialty.displayName,
        isChecked: false,
        isVisible: true,
        namespacePath: specialty.namespacePath,
        specialtyGroupCode: specialty.itemValue
      })));
      serviceAreaHasChanged.current = false;
      setLoading(false);
    };

    resourceFiltrationService.getFilterItemsInScope(makeParameterForSpecialties(), "§Specialties", true, PUBLISHED_ONLY, false, SHOW_ZIP_SUMMARIES_ONLY)
      .then(handleSpecialtiesResponse)
      .catch(() => {
        resourceFiltrationService.getFilterItems("§Specialties", true, PUBLISHED_ONLY, true, SHOW_ZIP_SUMMARIES_ONLY)
          .then(handleSpecialtiesResponse)
          .catch(() => {
            setError("We were unable to retrieve the list of specialty groups.");
          });
      });
  }

  const refreshFilteredProjects = () => {
    if (!serviceAreaHasChanged.current && !specialtyHasChanged.current && projects.length !== 0) {
      return;
    }
    setLoading(true);

    resourceFiltrationService.getVolumeExceptionsProjects(makeParameterForProjects()).then((projectResponse) => {
      setProjects(projectResponse.map((project) => ({ ...project, isVisible: true })));
      serviceAreaHasChanged.current = false;
      specialtyHasChanged.current = false;
      setLoading(false);
    }).catch(() => {
      setError("We were unable to retrieve the filtered list of projects.")
    });
  }

  const getServiceAreaFilter = () => {
    const serviceAreaFilter: FilterSetItem[] = [];

    if (!selectedServiceArea) {
      return serviceAreaFilter;
    }

    const [stateName, countyName, regionSetId] = selectedServiceArea.split(":");
    const state = serviceAreas.find(state => state.displayName === stateName);

    if (state && state.childServiceAreas) {
      const county = state.childServiceAreas.find(county => county.displayName === countyName && county.serviceAreaValue === parseInt(regionSetId));

      if (county) {
        serviceAreaFilter.push({ displayName: county.displayName, namespacePath: county.namespacePath });
      }
    }
    return serviceAreaFilter;
  }

  const getSpecialtyFilter = () => {
    const specialtyFilter: FilterSetItem[] = [];
    const [specialtyName, specialtyGroupCode] = selectedSpecialty.split(":");
    const specialty = specialties.find(sp => sp.displayName === specialtyName && sp.specialtyGroupCode === specialtyGroupCode);

    if (specialty) {
      specialtyFilter.push({ displayName: specialty.displayName, namespacePath: specialty.namespacePath });
    }
    return specialtyFilter;
  }

  const updateRegionVisibility = (filterString: string) => {
    const updatedList: CreateExceptionsState[] = structuredClone(serviceAreas);
    if (!filterString) {
      updatedList.forEach((state) => {
        state.isVisible = true;
        state.childServiceAreas.forEach((child) => {
          child.isVisible = true;
        })
      })
    } else {
      const regex = new RegExp(filterString, "i");
      updatedList.forEach((state) => {
        let hasMatchingCounty = false;
        state.childServiceAreas.forEach((county) => {
          const isMatch = !!county.displayName.match(regex);
          county.isVisible = isMatch;
          hasMatchingCounty = isMatch || hasMatchingCounty;
        });
        state.isVisible = hasMatchingCounty;
      });
    }
    setServiceAreas(updatedList);
  }

  const updateSpecialtiesVisibility = (filterString: string) => {
    const updatedList: CreateExceptionsSpecialty[] = structuredClone(specialties);
    if (!filterString) {
      updatedList.forEach((sp) => {
        sp.isVisible = true
      });
    } else {
      const regex = new RegExp(filterString, "i");
      updatedList.forEach((sp) => {
        sp.isVisible = !!sp.displayName.match(regex);
      });
    }
    setSpecialties(updatedList);
  }

  const updateProjectsVisibility = (filterString: string) => {
    const updatedList: CreateExceptionsProject[] = structuredClone(projects);
    if (!filterString) {
      updatedList.forEach((project) => {
        project.isVisible = true;
      });
    } else {
      const regex = new RegExp(filterString, "i");
      updatedList.forEach((project) => {
        project.isVisible = !!project.projectName.match(regex) || !!project.templateName.match(regex);
      });
    }
    setProjects(updatedList);
  }

  const updateSelectedServiceArea = (stateDisplayName: string, countyDisplayName: string, regionSetId: number) => {
    serviceAreaHasChanged.current = true;
    setSelectedServiceArea(`${stateDisplayName}:${countyDisplayName}:${regionSetId}`);
    resetFutureSteps(CreateExceptionsWizardContextSteps.ServiceArea);
  }

  const updateSelectedSpecialty = (specialtyDisplayName: string, specialtyGroupCode: string) => {
    specialtyHasChanged.current = true;
    setSelectedSpecialty(`${specialtyDisplayName}:${specialtyGroupCode}`);
    resetFutureSteps(CreateExceptionsWizardContextSteps.Specialty);
  }

  const updateSelectedProjects = (ids: string[]) => {
    const newProjects: CreateExceptionsProject[] = structuredClone(projects);
    newProjects.forEach((project) => {
      if (ids.includes(project.customProjectId.toString())) {
        project.isChecked = true;
      } else {
        project.isChecked = false;
      }
    });
    projectsHasChanged.current = true;
    resetFutureSteps(CreateExceptionsWizardContextSteps.Project);
    setProjects(newProjects);
  }

  const resetWizard = () => {
    getServiceAreas();
    setPage(CreateExceptionsWizardContextSteps.ServiceArea);
    setError("");
  }

  const resetFutureSteps = (currentStep: CreateExceptionsWizardContextSteps) => {
    function resetSpecialties() {
      setSpecialties([]);
      setSelectedSpecialty("");
      serviceAreaHasChanged.current = true;
    }

    function resetProjects() {
      setProjects([]);
    }

    function resetDetails() {
      setStatus(undefined);
      setStartDate("");
      setEndDate("");
      setPreviouslyApproved(false);
      setExcludeFromCCIIOJustification(false);
      setComment("");
      setNarrative("");
      setReviewStatus(undefined);
    }

    function resetAttachments() {
      setAttachments([]);
      setResetFileUploader(true);
    }

    switch (currentStep) {
      case CreateExceptionsWizardContextSteps.ServiceArea:
        resetSpecialties();
        resetProjects();
        resetDetails();
        resetAttachments();
        break;
      case CreateExceptionsWizardContextSteps.Specialty:
        resetProjects();
        resetDetails();
        resetAttachments();
        break;
      case CreateExceptionsWizardContextSteps.Project:
        resetDetails();
        resetAttachments();
        break;
    }
    setShowChangeWarning(false);
  }

  /* oxlint-disable sort-keys */
  const contextValue: CreateExceptionsContextProps = {
    attachments,
    error,
    loading,
    page,
    serviceAreas,
    selectedServiceArea,
    selectedSpecialty,
    projects,
    showChangeWarning,
    specialties,
    status,
    startDate,
    endDate,
    excludeFromCCIIOJustification,
    previouslyApproved,
    comment,
    narrative,
    reviewStatus,
    resetFileUploader,
    setPage,
    setAttachments,
    setResetFileUploader,
    setSelectedServiceArea,
    setSelectedSpecialty,
    setShowChangeWarning,
    setSpecialties,
    setStatus,
    setStartDate,
    setEndDate,
    setExcludeFromCCIIOJustification,
    setPreviouslyApproved,
    setComment,
    setNarrative,
    setReviewStatus,
    updateRegionVisibility,
    updateSpecialtiesVisibility,
    updateProjectsVisibility,
    updateSelectedServiceArea,
    updateSelectedSpecialty,
    updateSelectedProjects,
  };
  /* oxlint-enable sort-keys */

  return (
    <CreateExceptionsContext.Provider value={contextValue}>
      {props.children}
    </CreateExceptionsContext.Provider>
  );
};

export { CreateExceptionsContext, CreateExceptionsProvider }